import { Injectable , inject} from '@angular/core';
import { AppConfigService } from 'app/core/config/app-config.service';
import { FeatureSettingsService } from '@app/services/feature-settings.service';
import { BehaviorSubject, Observable } from 'rxjs';

export interface CourseDictionaryEntry {
  word: string;
  definitions: string[];
}

export interface GlobalAiChatConfig {
  environment: string;
  agentId: string;
  apiBaseUrl: string;  // Required - no fallback
  content?: string;
  lang: string;  // Always "en"
  contentLang: string;  // Always "en"
  role: 'child' | 'executive';  // Default from this.appConfig.get('ai_chat_role')
  autoStart: boolean;  // Always true for sidebar (connect once)
  muteAi: boolean;  // Mute when in listener mode or switching
  debug: boolean;
  fullMode: boolean;
  showWidget: boolean;
  /** Monotonic visit id — every step open (incl. back) bumps this so the widget always applies config. */
  flowSeq?: number;
  /** Alias for hosts/logs that prefer stepVisitId naming. */
  stepVisitId?: number;
}

@Injectable({
  providedIn: 'root'
})
export class GlobalAiChatService {
  readonly appConfig = inject(AppConfigService);
  private readonly featureSettings = inject(FeatureSettingsService);
  // Get apiBaseUrl (return empty string if not configured – AI chat will be disabled)
  private getApiBaseUrl(): string {
    return this.appConfig.get('ai_api_baseurl_chat') || '';
  }

  private defaultConfig: GlobalAiChatConfig = {
    environment: "webcomponent", // so we no that the data comes from a webcomponent in react ai-chat
    agentId: 'agent.listener',  // ← Start with listener agent
    apiBaseUrl: this.getApiBaseUrl(),
    lang: 'en',  // ← Always "en"
    contentLang: 'en',  // ← Always "en"
    role: (this.appConfig.get('ai_chat_role') as 'child' | 'executive') || 'executive',  // ← From environment, default 'executive'
    autoStart: true,  // ← Always true - connect once at startup
    muteAi: true,  // ← Mute in listener mode (silent agent)
    debug: false,
    fullMode: true,
    showWidget: true,
    flowSeq: 0,
    stepVisitId: 0,
  };

  // Current widget configuration (observable)
  private configSubject = new BehaviorSubject<GlobalAiChatConfig>(this.defaultConfig);
  public config$: Observable<GlobalAiChatConfig> = this.configSubject.asObservable();

  /** Monotonic counter for every config push (step open / back / next). */
  private flowSeqCounter = 0;

  /**
   * Debounce reset→listener so step ngOnDestroy does not briefly hop to
   * listener before the next step's updateConfig arrives.
   */
  private resetToListenerTimer: ReturnType<typeof setTimeout> | null = null;

  /** Timestamp of last updateConfig — used to ignore stale destroy resets. */
  private lastUpdateConfigAt = 0;

  /** Lesson worker agent is active — Done waits for ai-chat-flow-terminal. */
  private aiFlowActive = false;

  /**
   * First AI worker step after entering academy uses welcome-back (resume);
   * later steps in the same visit use enter (no re-welcome).
   */
  private academyWelcomeUsed = false;

  /** Course title for resume welcome-back lines. */
  private academyCourseTitle = '';

  /**
   * True while the course-entry “Welcome back…” line is the active flow.
   * Blocks resetToListener / defers later step configs until it finishes.
   */
  private courseEntryWelcomeActive = false;

  /** student_course_step_id the course-entry welcome was spoken for. */
  private courseEntryWelcomeStepId: string | null = null;

  /** Latest step_id from the academy URL (compact layout). */
  private activeStepId: string | null = null;

  /** Step config that arrived while course-entry welcome was still speaking. */
  private pendingConfigAfterWelcome: Partial<GlobalAiChatConfig> | null = null;

  /** Step id that owns pendingConfigAfterWelcome (if known). */
  private pendingConfigStepId: string | null = null;

  /** Safety: if welcome never terminals (session stuck), unblock deferred steps. */
  private courseEntryWelcomeTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Visit-scoped course lexicon accumulated from step payloads (word +
   * definition). Injected into every AI content JSON so the widget may only
   * use course definitions — never an external dictionary.
   */
  private courseDictionary: CourseDictionaryEntry[] = [];

  private readyForDoneSubject = new BehaviorSubject<boolean>(true);
  public readyForDone$: Observable<boolean> = this.readyForDoneSubject.asObservable();

  private get isEnabled(): boolean {
    return this.featureSettings.isFeatureEnabled('feature_ai');
  }

  constructor() {
    if (this.isEnabled) {
      console.log('🎯 GlobalAiChatService initialized with listener agent');
      console.log('📍 API Base URL:', this.defaultConfig.apiBaseUrl);
      console.log('🔇 Listener mode: Muted (silent agent)');
    }
  }

  private upsertCourseDictionary(word: string, definitions: string[]): void {
    const w = String(word || '').trim();
    const defs = (definitions || [])
      .map((d) => String(d || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim())
      .filter(Boolean);
    if (!w || !defs.length) return;
    const key = w.toLowerCase();
    const existing = this.courseDictionary.find((e) => e.word.toLowerCase() === key);
    if (!existing) {
      this.courseDictionary.push({ word: w, definitions: [...defs] });
      return;
    }
    for (const d of defs) {
      if (!existing.definitions.some((x) => x.toLowerCase() === d.toLowerCase())) {
        existing.definitions.push(d);
      }
    }
  }

  /**
   * Ingest word/definition from step content and attach the visit lexicon.
   */
  private ingestAndAttachCourseDictionary(content?: string): string | undefined {
    if (!content || !String(content).trim()) return content;
    try {
      const parsed = JSON.parse(content);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return content;
      }

      if (Array.isArray(parsed.courseDictionary)) {
        for (const entry of parsed.courseDictionary) {
          if (entry?.word && Array.isArray(entry.definitions)) {
            this.upsertCourseDictionary(String(entry.word), entry.definitions.map(String));
          }
        }
      }

      const word = typeof parsed.word === 'string' ? parsed.word.trim() : '';
      const defs: string[] = [];
      if (typeof parsed.definition === 'string' && parsed.definition.trim()) {
        defs.push(parsed.definition);
      }
      if (typeof parsed.definitionHtml === 'string' && parsed.definitionHtml.trim()) {
        defs.push(parsed.definitionHtml);
      }
      if (Array.isArray(parsed.definitions)) {
        for (const d of parsed.definitions) {
          if (typeof d === 'string' && d.trim()) defs.push(d);
        }
      }
      // Infer word from common command phrasing when only a definition is present.
      let resolvedWord = word;
      if (!resolvedWord && typeof parsed.command === 'string') {
        const m = parsed.command.match(/\bword\s+["']?([A-Za-z-]+)["']?/i);
        if (m) resolvedWord = m[1];
      }
      if (resolvedWord && defs.length) {
        this.upsertCourseDictionary(resolvedWord, defs);
      }

      if (this.courseDictionary.length) {
        parsed.courseDictionary = this.courseDictionary.map((e) => ({
          word: e.word,
          definitions: [...e.definitions],
        }));
        return JSON.stringify(parsed);
      }
      return content;
    } catch {
      return content;
    }
  }

  getCourseDictionary(): CourseDictionaryEntry[] {
    return this.courseDictionary.map((e) => ({
      word: e.word,
      definitions: [...e.definitions],
    }));
  }

  /**
   * Mute / showWidget-only updates must not bump flowSeq — that re-accepts
   * the previous step's agent+command and re-speaks it after navigation.
   */
  private isCosmeticConfigUpdate(config: Partial<GlobalAiChatConfig>): boolean {
    const keys = Object.keys(config).filter(
      (k) => (config as Record<string, unknown>)[k] !== undefined
    );
    if (keys.length === 0) return true;
    const cosmetic = new Set([
      'muteAi',
      'showWidget',
      'debug',
      'fullMode',
      'environment',
      'lang',
      'contentLang',
      'role',
      'autoStart',
      'apiBaseUrl',
    ]);
    return keys.every((k) => cosmetic.has(k));
  }

  /** Track the step currently shown in the academy secondary outlet. */
  setActiveStepId(stepId: string | null | undefined): void {
    if (!this.isEnabled) return;
    const next = stepId != null && String(stepId).trim() !== '' ? String(stepId) : null;
    // While course-entry welcome owns a step, ignore stale URL step ids from
    // the previous session until the router catches up to the open step.
    if (
      next &&
      this.courseEntryWelcomeActive &&
      this.courseEntryWelcomeStepId &&
      next !== this.courseEntryWelcomeStepId
    ) {
      console.log('[StepAI] ignore activeStepId — course entry welcome owns step', {
        ignored: next,
        welcomeStepId: this.courseEntryWelcomeStepId,
      });
      return;
    }
    this.activeStepId = next;
  }

  /**
   * Step navigation: drop deferred Gallery/etc configs and park on a silent
   * listener with empty content so the previous command cannot speak again.
   */
  notifyStepIdChanged(
    previousStepId: string | null,
    newStepId: string
  ): void {
    if (!this.isEnabled) return;
    this.activeStepId = newStepId;

    // Stale deferred configs from a previous outlet must never survive navigation.
    if (
      this.pendingConfigAfterWelcome &&
      (!this.pendingConfigStepId || this.pendingConfigStepId !== newStepId)
    ) {
      console.log('[StepAI] drop pendingConfigAfterWelcome — step changed', {
        previousStepId,
        newStepId,
        pendingStepId: this.pendingConfigStepId,
      });
      this.pendingConfigAfterWelcome = null;
      this.pendingConfigStepId = null;
    }

    // Landing on the step we just welcomed for (URL catch-up after checksheet
    // navigate) must not cut the welcome mid-line.
    if (
      this.courseEntryWelcomeActive &&
      this.courseEntryWelcomeStepId &&
      this.courseEntryWelcomeStepId === newStepId
    ) {
      console.log('[StepAI] keep course entry welcome — navigated onto welcomed step', {
        previousStepId,
        newStepId,
      });
      return;
    }

    if (this.courseEntryWelcomeActive) {
      this.courseEntryWelcomeActive = false;
      this.courseEntryWelcomeStepId = null;
      if (this.courseEntryWelcomeTimer != null) {
        clearTimeout(this.courseEntryWelcomeTimer);
        this.courseEntryWelcomeTimer = null;
      }
      console.log('[StepAI] release course entry welcome — step changed', {
        previousStepId,
        newStepId,
      });
    }
    this.updateConfig({
      agentId: 'agent.listener',
      muteAi: true,
      content: '',
    });
  }

  /**
   * Update widget configuration (called by step components)
   * Simplified: Directly applies config without listener transfer
   * Disconnect/reconnect is handled by layout component on step changes
   */
  updateConfig(
    config: Partial<GlobalAiChatConfig> & { sourceStepId?: string | number | null }
  ): void {
    if (!this.isEnabled) return;

    const sourceStepId =
      config.sourceStepId != null && String(config.sourceStepId).trim() !== ''
        ? String(config.sourceStepId)
        : null;
    // Strip host-only field — never push it into widget config.
    const { sourceStepId: _sourceStepId, ...configRest } = config;
    config = configRest;

    const cosmeticOnly = this.isCosmeticConfigUpdate(config);

    // Ignore zombie step components that finish loading after navigation.
    if (
      !cosmeticOnly &&
      sourceStepId &&
      this.activeStepId &&
      sourceStepId !== this.activeStepId
    ) {
      console.log('[StepAI] ignore config — not current step', {
        sourceStepId,
        activeStepId: this.activeStepId,
        agentId: config.agentId,
      });
      return;
    }

    // Do not cut the course-entry welcome mid-line. Queue the next step
    // config and apply it when resume_v1 terminals.
    // Cosmetic mute/showWidget updates may apply immediately (no re-speak).
    if (
      !cosmeticOnly &&
      this.courseEntryWelcomeActive &&
      !this.contentHasCourseEntry(config.content)
    ) {
      // Require a step id during welcome — otherwise a zombie outlet can
      // poison pending and speak the previous session's agent after resume.
      if (!sourceStepId) {
        console.log('[StepAI] ignore deferred config — missing sourceStepId', {
          agentId: config.agentId,
          welcomeStepId: this.courseEntryWelcomeStepId,
        });
        return;
      }
      if (
        this.courseEntryWelcomeStepId &&
        sourceStepId !== this.courseEntryWelcomeStepId
      ) {
        console.log('[StepAI] ignore deferred config — wrong step for welcome', {
          sourceStepId,
          welcomeStepId: this.courseEntryWelcomeStepId,
          agentId: config.agentId,
        });
        return;
      }
      this.pendingConfigAfterWelcome = {
        ...(this.pendingConfigAfterWelcome || {}),
        ...config,
      };
      this.pendingConfigStepId = sourceStepId;
      console.log('[StepAI] defer config until course entry welcome finishes', {
        agentId: config.agentId,
        muteAi: config.muteAi,
        sourceStepId: this.pendingConfigStepId,
      });
      return;
    }

    this.cancelPendingResetToListener();
    this.lastUpdateConfigAt = Date.now();

    const currentConfig = this.configSubject.value;
    
    // Determine if we should mute based on agent
    const shouldMute = this.shouldMuteForAgent(config.agentId || currentConfig.agentId);

    const visitId = cosmeticOnly
      ? currentConfig.flowSeq ?? this.flowSeqCounter
      : (++this.flowSeqCounter);

    const contentWithDict =
      config.content !== undefined
        ? this.ingestAndAttachCourseDictionary(config.content)
        : currentConfig.content;
    
    // Ensure lang and contentLang are always "en"
    const newConfig: GlobalAiChatConfig = {
      ...currentConfig,
      ...config,
      content: contentWithDict,
      lang: 'en',  // ← Always "en"
      contentLang: 'en',  // ← Always "en"
      role: config.role || (this.appConfig.get('ai_chat_role') as 'child' | 'executive') || 'executive',  // ← From environment, default 'executive'
      autoStart: true,  // ← Always true (will reconnect)
      muteAi: config.muteAi !== undefined ? config.muteAi : shouldMute,
      apiBaseUrl: config.apiBaseUrl || currentConfig.apiBaseUrl,
      flowSeq: visitId,
      stepVisitId: visitId,
    };
    
    // Warn if apiBaseUrl is not set (AI chat will not function)
    if (!newConfig.apiBaseUrl) {
      console.warn('⚠️ ai_api_baseurl_chat is not configured – AI chat disabled');
    }

    const agentId = newConfig.agentId || 'agent.listener';
    this.aiFlowActive = agentId !== 'agent.listener';
    // New AI step: Done gated until flow terminal. Listener / non-AI: allow Done.
    this.readyForDoneSubject.next(!this.aiFlowActive);
    
    console.log('[StepAI] config.push', {
      agentId: newConfig.agentId,
      flowSeq: visitId,
      cosmeticOnly,
      muteAi: newConfig.muteAi,
      hasContent: !!newConfig.content,
      contentLength: newConfig.content?.length || 0,
      contentPreview: newConfig.content ? newConfig.content.substring(0, 80) : undefined,
      aiFlowActive: this.aiFlowActive,
      readyForDone: this.readyForDoneSubject.value,
    });
    
    this.configSubject.next(newConfig);
  }

  /**
   * Determine if agent should be muted
   * Listener agent is always muted (silent)
   */
  private shouldMuteForAgent(agentId: string): boolean {
    // Listener agent is silent - always mute
    if (agentId === 'agent.listener') {
      return true;
    }
    // All other agents are active - don't mute by default
    return false;
  }

  /**
   * Reset to default (listener agent) - called when leaving step components
   * Mutes the widget (listener is silent).
   * Debounced so destroy→next-step updateConfig cancels a useless listener hop.
   */
  resetToListener(): void {
    if (!this.isEnabled) return;
    if (this.courseEntryWelcomeActive) {
      console.log('[StepAI] skip resetToListener — course entry welcome active');
      return;
    }
    this.cancelPendingResetToListener();
    // Outlet races (FinalTest mount/destroy while Gallery already pushed
    // agent.welcome) must not wipe the winner. Skip if updateConfig ran
    // recently OR after this reset was scheduled.
    const scheduledAt = Date.now();
    const seqAtSchedule = this.flowSeqCounter;
    // Long enough that the next step's updateConfig (after API load) cancels
    // this hop — otherwise we cutover to listener and interrupt the session
    // right before the real step agent starts speaking.
    this.resetToListenerTimer = setTimeout(() => {
      this.resetToListenerTimer = null;
      const current = this.configSubject.value;
      if (this.flowSeqCounter !== seqAtSchedule) {
        console.log('[StepAI] skip resetToListener — superseded by newer config', {
          seqAtSchedule,
          now: this.flowSeqCounter,
          currentAgent: current.agentId,
        });
        return;
      }
      if (this.lastUpdateConfigAt > scheduledAt) {
        console.log('[StepAI] skip resetToListener — updateConfig after schedule', {
          lastUpdateConfigAt: this.lastUpdateConfigAt,
          scheduledAt,
          currentAgent: current.agentId,
        });
        return;
      }
      // Destroy of a losing outlet often fires AFTER the winning step already
      // configured. Protect a fresh non-listener config (~2s window).
      const msSinceUpdate = Date.now() - this.lastUpdateConfigAt;
      if (
        current.agentId &&
        current.agentId !== 'agent.listener' &&
        current.content &&
        msSinceUpdate >= 0 &&
        msSinceUpdate < 2000
      ) {
        console.log('[StepAI] skip resetToListener — recent step config', {
          currentAgent: current.agentId,
          msSinceUpdate,
        });
        return;
      }
      console.log('[StepAI] Resetting to listener agent (muted)');
      this.flowSeqCounter += 1;
      const visitId = this.flowSeqCounter;
      this.aiFlowActive = false;
      this.readyForDoneSubject.next(true);
      this.configSubject.next({
        ...this.defaultConfig,
        // Stay visible in the academy drawer; only mute.
        showWidget: current.showWidget !== false,
        muteAi: true,
        flowSeq: visitId,
        stepVisitId: visitId,
      });
    }, 500);
  }

  private cancelPendingResetToListener(): void {
    if (this.resetToListenerTimer != null) {
      clearTimeout(this.resetToListenerTimer);
      this.resetToListenerTimer = null;
    }
  }

  /** Called from layout when widget emits ai-chat-flow-terminal. */
  setReadyForDone(ready: boolean, detail?: { flowId?: string; reason?: string }): void {
    if (!this.isEnabled) return;
    this.readyForDoneSubject.next(!!ready);
    console.log('[StepAI] readyForDone', {
      ready: !!ready,
      aiFlowActive: this.aiFlowActive,
      flowId: detail?.flowId,
      reason: detail?.reason,
    });
    const flowId = String(detail?.flowId || '');
    const reason = String(detail?.reason || '');
    // Prefer natural resume completion over the host timeout cutover.
    if (
      this.courseEntryWelcomeActive &&
      (flowId === 'resume_v1' || flowId === 'resume') &&
      (ready || reason === 'resume_completed')
    ) {
      this.clearCourseEntryWelcome('resume_terminal');
    }
  }

  /** True when Done may proceed (non-AI, or AI flow reached terminal). */
  isDoneAllowed(): boolean {
    if (!this.isEnabled || !this.aiFlowActive) return true;
    // AI guidance is optional — only gate Done when the company opted in.
    if (!this.featureSettings.isFeatureEnabled('feature_ai_step_gate')) return true;
    return this.readyForDoneSubject.value === true;
  }

  isAiFlowActive(): boolean {
    return this.aiFlowActive;
  }

  /**
   * Mute AI audio (without changing agent)
   */
  mute(): void {
    if (!this.isEnabled) return;
    this.updateConfig({ muteAi: true });
    console.log('🔇 AI muted');
  }

  /**
   * Unmute AI audio (without changing agent)
   */
  unmute(): void {
    if (!this.isEnabled) return;
    const current = this.configSubject.value;
    // Only unmute if not in listener mode
    if (current.agentId !== 'agent.listener') {
      this.updateConfig({ muteAi: false });
      console.log('🔊 AI unmuted');
    } else {
      console.log('⚠️ Cannot unmute - in listener mode (silent agent)');
    }
  }

  /**
   * Toggle mute state
   */
  toggleMute(): void {
    if (!this.isEnabled) return;
    const current = this.configSubject.value;
    if (current.muteAi) {
      this.unmute();
    } else {
      this.mute();
    }
  }

  /**
   * Get current config (synchronous)
   */
  getCurrentConfig(): GlobalAiChatConfig {
    return this.configSubject.value;
  }

  /**
   * Peek resume vs enter without consuming. Call markAcademyWelcomeUsed()
   * only after a real AI worker config is successfully pushed (not listener /
   * non-AI / failed build).
   */
  peekAcademyStepEntry(): 'resume' | 'enter' {
    if (!this.isEnabled) return 'enter';
    return this.academyWelcomeUsed ? 'enter' : 'resume';
  }

  /** One course welcome-back per academy visit. */
  markAcademyWelcomeUsed(): void {
    this.academyWelcomeUsed = true;
  }

  /**
   * Speak course welcome-back as soon as the student enters /academy/*,
   * regardless of step type (FinalTest, Gallery, …). Marks the visit so
   * later AI steps do not repeat “Welcome back to the course …”.
   */
  speakCourseEntryWelcome(step?: {
    title?: string | null;
    caption?: string | null;
    ai_prompt?: string | null;
    step_type?: string | null;
    stepId?: string | number | null;
  } | null): void {
    if (!this.isEnabled) return;
    if (this.academyWelcomeUsed) return;
    const course = this.academyCourseTitle;
    if (!course) {
      console.warn('[StepAI] course entry welcome skipped — no course title yet');
      return;
    }

    const welcomeStepId =
      step?.stepId != null && String(step.stepId).trim() !== ''
        ? String(step.stepId)
        : null;

    const strip = (s: string) =>
      String(s || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    const stepLabel =
      strip(step?.caption || '') ||
      strip(step?.title || '') ||
      'this step';

    let todo = '';
    const rawPrompt = step?.ai_prompt;
    if (rawPrompt) {
      try {
        const parsed = JSON.parse(String(rawPrompt));
        if (parsed && typeof parsed.command === 'string') {
          todo = strip(parsed.command);
        }
      } catch {
        /* plain text prompt */
        todo = strip(String(rawPrompt)).slice(0, 220);
      }
    }
    // Drop first-visit hellos — this is a resume welcome.
    const lowerTodo = todo.toLowerCase();
    if (
      lowerTodo.startsWith('hello!') ||
      lowerTodo.startsWith('hello ') ||
      lowerTodo.includes("today we're going to learn") ||
      lowerTodo.includes('today we are going to learn')
    ) {
      todo = '';
    }
    if (!todo) {
      const kind = strip(step?.step_type || step?.caption || '');
      todo = kind
        ? `Complete ${kind} when you are ready.`
        : 'Continue with this step when you are ready.';
    }

    const placeClause =
      stepLabel.toLowerCase() === 'this step'
        ? ''
        : ` We are on ${stepLabel} right now.`;
    const command =
      `Welcome back to the course ${course}.${placeClause} ${todo}`
        .replace(/\s+/g, ' ')
        .trim();

    this.academyWelcomeUsed = true;
    this.courseEntryWelcomeActive = true;
    this.courseEntryWelcomeStepId = welcomeStepId;
    this.pendingConfigAfterWelcome = null;
    this.pendingConfigStepId = null;
    // Prefer the checksheet-open step as soon as we know it — blocks stale
    // outlet configs from the previous URL step while router catches up.
    if (welcomeStepId) {
      this.activeStepId = welcomeStepId;
    }
    this.cancelPendingResetToListener();
    if (this.courseEntryWelcomeTimer != null) {
      clearTimeout(this.courseEntryWelcomeTimer);
    }
    // If the widget never flushes/speaks (connect race), do not block forever.
    // While CONNECTED + resume is still speaking, extend instead of cutting over
    // to muted listener mid-line (was forcing FinalTest quiet while audio drained).
    const welcomeArmedAt = Date.now();
    const MAX_COURSE_ENTRY_MS = 90_000;
    const armCourseEntryWelcomeTimer = (ms: number) => {
      if (this.courseEntryWelcomeTimer != null) {
        clearTimeout(this.courseEntryWelcomeTimer);
      }
      this.courseEntryWelcomeTimer = setTimeout(() => {
        this.courseEntryWelcomeTimer = null;
        if (!this.courseEntryWelcomeActive) return;
        const status = String(
          (window as any)?.__aiChatZustandState?.sessionStatus ||
            (window as any)?.__useAppState?.getState?.()?.sessionStatus ||
            ''
        );
        const connected = status === 'CONNECTED';
        const elapsed = Date.now() - welcomeArmedAt;
        if (connected && elapsed < MAX_COURSE_ENTRY_MS) {
          console.warn(
            '[StepAI] course entry welcome still active while CONNECTED — extending deferral',
            { elapsed }
          );
          armCourseEntryWelcomeTimer(20_000);
          return;
        }
        console.warn('[StepAI] course entry welcome timed out — releasing deferral', {
          elapsed,
          connected,
        });
        this.clearCourseEntryWelcome('timeout');
      }, ms);
    };
    armCourseEntryWelcomeTimer(25_000);
    this.updateConfig({
      agentId: 'agent.welcome',
      muteAi: false,
      content: JSON.stringify({
        command,
        stepTitle: stepLabel,
        entryReason: 'resume',
        courseEntry: true,
      }),
      sourceStepId: welcomeStepId,
    });
    console.log('[StepAI] course entry welcome pushed', {
      course,
      stepLabel,
      welcomeStepId,
      commandPreview: command.slice(0, 120),
    });
  }

  /** True while course-entry welcome must not be interrupted. */
  isCourseEntryWelcomeActive(): boolean {
    return this.courseEntryWelcomeActive;
  }

  private contentHasCourseEntry(content?: string | null): boolean {
    if (!content) return false;
    try {
      const parsed = JSON.parse(content);
      return parsed?.courseEntry === true;
    } catch {
      return false;
    }
  }

  /**
   * End course-entry protection and apply any deferred step config
   * (or fall back to muted listener on non-AI landings like FinalTest).
   */
  clearCourseEntryWelcome(reason = 'manual'): void {
    if (!this.courseEntryWelcomeActive && !this.pendingConfigAfterWelcome) return;
    this.courseEntryWelcomeActive = false;
    if (this.courseEntryWelcomeTimer != null) {
      clearTimeout(this.courseEntryWelcomeTimer);
      this.courseEntryWelcomeTimer = null;
    }
    const pending = this.pendingConfigAfterWelcome;
    const pendingStepId = this.pendingConfigStepId;
    const welcomeStepId = this.courseEntryWelcomeStepId;
    this.pendingConfigAfterWelcome = null;
    this.pendingConfigStepId = null;
    this.courseEntryWelcomeStepId = null;
    // Strict: only apply deferred config when it is tagged for the welcomed step.
    const pendingMatchesWelcome =
      !!pending &&
      !!pendingStepId &&
      !!welcomeStepId &&
      pendingStepId === welcomeStepId &&
      (!this.activeStepId || pendingStepId === this.activeStepId);
    console.log('[StepAI] course entry welcome cleared', {
      reason,
      hasPending: !!pending,
      pendingStepId,
      welcomeStepId,
      activeStepId: this.activeStepId,
      applyPending: pendingMatchesWelcome,
    });
    if (pending && pendingMatchesWelcome) {
      // Brief settle so the last words of course-entry welcome can finish
      // draining before a deferred step config cutover interrupts audio.
      setTimeout(() => {
        if (this.courseEntryWelcomeActive) return;
        this.updateConfig({
          ...pending,
          sourceStepId: pendingStepId,
        });
      }, 500);
    } else {
      if (pending && !pendingMatchesWelcome) {
        console.log('[StepAI] drop pending after welcome — step mismatch', {
          pendingStepId,
          welcomeStepId,
          activeStepId: this.activeStepId,
        });
      }
      // Non-AI landing (FinalTest / drill): keep the welcome orientation as
      // listener context instead of wiping content to length 0.
      this.settleQuietListenerAfterWelcome();
    }
  }

  /**
   * After course-entry welcome on a step with no AI worker config: stay on the
   * muted listener but keep the welcome command so the AI still knows where
   * the student is.
   */
  private settleQuietListenerAfterWelcome(): void {
    const current = this.configSubject.value;
    let content = current.content;
    try {
      const parsed = JSON.parse(String(current.content || ''));
      if (parsed && typeof parsed === 'object') {
        content = JSON.stringify({
          command: parsed.command || undefined,
          stepTitle: parsed.stepTitle || undefined,
          entryReason: 'enter',
        });
      }
    } catch {
      /* keep as-is */
    }
    this.aiFlowActive = false;
    this.readyForDoneSubject.next(true);

    // Same settle as the deferred-config branch above. This runs inside the
    // synchronous ai-chat-flow-terminal emit, so pushing muteAi:true here
    // would re-enter the widget from inside completeFlow and hard-mute the
    // last words of the welcome.
    setTimeout(() => {
      if (this.courseEntryWelcomeActive) return;
      // A newer step config landed while we settled — it owns the widget now.
      if (this.configSubject.value !== current) return;
      this.cancelPendingResetToListener();
      this.flowSeqCounter += 1;
      const visitId = this.flowSeqCounter;
      this.lastUpdateConfigAt = Date.now();
      this.configSubject.next({
        ...this.defaultConfig,
        apiBaseUrl: current.apiBaseUrl || this.defaultConfig.apiBaseUrl,
        showWidget: current.showWidget !== false,
        muteAi: true,
        content,
        flowSeq: visitId,
        stepVisitId: visitId,
      });
      console.log('[StepAI] course entry welcome → quiet listener', {
        contentLength: content?.length || 0,
      });
    }, 500);
  }

  /**
   * @deprecated Prefer peekAcademyStepEntry + markAcademyWelcomeUsed so
   * listener / failed builds do not burn the resume latch.
   */
  consumeAcademyStepEntry(): 'resume' | 'enter' {
    return this.peekAcademyStepEntry();
  }

  /** Current course title for “Welcome back to the course …”. */
  setAcademyCourseTitle(title: string | null | undefined): void {
    this.academyCourseTitle = String(title || '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  getAcademyCourseTitle(): string {
    return this.academyCourseTitle;
  }

  /** Reset when leaving /academy/* so the next course entry can welcome again. */
  resetAcademyVisit(): void {
    this.academyWelcomeUsed = false;
    this.courseDictionary = [];
    this.academyCourseTitle = '';
    this.courseEntryWelcomeActive = false;
    this.courseEntryWelcomeStepId = null;
    this.activeStepId = null;
    this.pendingConfigAfterWelcome = null;
    this.pendingConfigStepId = null;
    if (this.courseEntryWelcomeTimer != null) {
      clearTimeout(this.courseEntryWelcomeTimer);
      this.courseEntryWelcomeTimer = null;
    }
  }

  /**
   * Check if currently muted
   */
  isMuted(): boolean {
    return this.configSubject.value.muteAi;
  }

  /**
   * Clear widget (hide it)
   */
  hideWidget(): void {
    if (!this.isEnabled) return;
    this.updateConfig({ showWidget: false });
  }

  /**
   * Show widget
   */
  showWidget(): void {
    if (!this.isEnabled) return;
    this.updateConfig({ showWidget: true });
  }
}
